import type { MagazineResult } from '@creanizer/types'
import '../styles/documentStyles.css'

interface MagazineViewerProps {
  document: MagazineResult
  visualStyle?: string
}

export function MagazineViewer({ document, visualStyle = 'modern' }: MagazineViewerProps) {
  // データ検証
  if (!document || !document.title || !document.body) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">雑誌記事</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // 本文の段落分割
  const formatBody = (body: string): string[] => {
    return body.split('\n\n').filter(p => p.trim())
  }
  
  // ファッション誌スタイル
  if (visualStyle === 'fashion') {
    return (
      <div className="max-w-4xl mx-auto p-8 bg-gradient-to-br from-pink-50 to-purple-50 shadow-lg">
        <header className="text-center mb-8">
          <div className="text-sm font-bold tracking-widest text-purple-600 mb-2">
            {document.publication || 'VOGUE'}
          </div>
          {document.issue && (
            <div className="text-xs text-gray-600 mb-4">{document.issue}</div>
          )}
          <h1 className="text-5xl font-thin tracking-wide text-gray-900 mb-2">
            {document.title}
          </h1>
          {document.subtitle && (
            <p className="text-lg italic text-gray-700">{document.subtitle}</p>
          )}
        </header>
        
        {document.lead && (
          <p className="text-lg leading-relaxed mb-6 font-light">
            {document.lead}
          </p>
        )}
        
        <div className="columns-2 gap-8">
          {formatBody(document.body).map((paragraph, index) => (
            <p key={index} className="mb-4 text-justify leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
        
        {document.pullQuotes && document.pullQuotes.length > 0 && (
          <div className="mt-8 space-y-4">
            {document.pullQuotes.map((quote, index) => (
              <blockquote key={index} className="text-2xl font-light italic text-center text-purple-700 py-4 border-t border-b border-purple-200">
                "{quote}"
              </blockquote>
            ))}
          </div>
        )}
      </div>
    )
  }
  
  // ビジネス誌スタイル
  if (visualStyle === 'business') {
    return (
      <div className="max-w-5xl mx-auto p-8 bg-white shadow-lg">
        <header className="border-b-2 border-gray-800 pb-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-bold text-gray-800">
              {document.publication || '日経ビジネス'}
            </span>
            <span className="text-sm text-gray-600">{document.date}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {document.title}
          </h1>
          {document.subtitle && (
            <p className="text-lg text-gray-700">{document.subtitle}</p>
          )}
        </header>
        
        {document.lead && (
          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
            <p className="text-lg font-medium">{document.lead}</p>
          </div>
        )}
        
        <div className="prose max-w-none">
          {formatBody(document.body).map((paragraph, index) => (
            <p key={index} className="mb-4 text-gray-800 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
        
        {document.highlights && document.highlights.length > 0 && (
          <div className="mt-6 bg-gray-100 p-4 rounded">
            <h3 className="font-bold text-gray-900 mb-3">ポイント</h3>
            <ul className="space-y-2">
              {document.highlights.map((highlight, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-600 mr-2">▶</span>
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }
  
  // インタビュー誌スタイル
  if (visualStyle === 'interview') {
    return (
      <div className="max-w-4xl mx-auto p-8 bg-gradient-to-b from-gray-50 to-white shadow-lg">
        <header className="text-center mb-8 pb-6 border-b-2 border-gray-300">
          <div className="text-sm font-semibold text-gray-600 mb-2">
            {document.publication || 'INTERVIEW MAGAZINE'}
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {document.title}
          </h1>
          {document.subtitle && (
            <p className="text-xl italic text-gray-700">{document.subtitle}</p>
          )}
          {document.author && (
            <div className="mt-4 text-sm text-gray-600">
              <span>Interview by </span>
              <span className="font-semibold">{document.author.name}</span>
            </div>
          )}
        </header>
        
        {document.lead && (
          <p className="text-lg italic mb-6 text-gray-700 leading-relaxed">
            {document.lead}
          </p>
        )}
        
        <div className="space-y-4">
          {formatBody(document.body).map((paragraph, index) => (
            <p key={index} className="leading-relaxed text-gray-800">
              {paragraph}
            </p>
          ))}
        </div>
        
        {document.sidebars && document.sidebars.length > 0 && (
          <div className="mt-8 space-y-4">
            {document.sidebars.map((sidebar, index) => (
              <div key={index} className="bg-gray-100 p-4 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">{sidebar.title}</h4>
                <p className="text-sm text-gray-700">{sidebar.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
  
  // デフォルト（現代的な雑誌）
  return (
    <div className="max-w-5xl mx-auto p-8 bg-white shadow-lg">
      <header className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              {document.publication || 'MAGAZINE'}
            </div>
            {document.issue && (
              <div className="text-xs text-gray-500">{document.issue}</div>
            )}
          </div>
          <div className="text-sm text-gray-600">{document.date}</div>
        </div>
        
        <h1 className="text-4xl font-bold text-gray-900 mb-3 leading-tight">
          {document.title}
        </h1>
        
        {document.subtitle && (
          <p className="text-xl text-gray-700 italic">
            {document.subtitle}
          </p>
        )}
        
        {document.author && (
          <div className="mt-4 text-sm text-gray-600">
            <span>文・</span>
            <span className="font-medium">{document.author.name}</span>
            {document.author.profile && (
              <span className="ml-2 text-xs">（{document.author.profile}）</span>
            )}
          </div>
        )}
      </header>
      
      {document.lead && (
        <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-white border-l-4 border-gray-400">
          <p className="text-lg leading-relaxed text-gray-800">
            {document.lead}
          </p>
        </div>
      )}
      
      <div className="prose max-w-none">
        {formatBody(document.body).map((paragraph, index) => (
          <p key={index} className="mb-4 text-gray-800 leading-relaxed">
            {paragraph}
          </p>
        ))}
      </div>
      
      {document.pullQuotes && document.pullQuotes.length > 0 && (
        <div className="my-8 space-y-4">
          {document.pullQuotes.map((quote, index) => (
            <blockquote key={index} className="text-xl italic text-center text-gray-700 py-4 px-8 border-l-4 border-r-4 border-gray-300">
              "{quote}"
            </blockquote>
          ))}
        </div>
      )}
      
      {document.highlights && document.highlights.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-bold text-gray-900 mb-3">KEY POINTS</h3>
          <ul className="space-y-2">
            {document.highlights.map((highlight, index) => (
              <li key={index} className="flex items-start">
                <span className="text-gray-500 mr-2">●</span>
                <span className="text-gray-800">{highlight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {document.imageDescriptions && document.imageDescriptions.length > 0 && (
        <div className="mt-6 space-y-3">
          {document.imageDescriptions.map((img, index) => (
            <div key={index} className="border border-gray-200 p-3 rounded">
              <div className="text-xs text-gray-500 mb-1">[画像{index + 1}]</div>
              <div className="text-sm font-medium text-gray-700">{img.caption}</div>
              <div className="text-xs text-gray-600 mt-1">{img.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}